import { Transaction, Document, AttachedItem } from "../dndTypes.ts";
import { cleanDocumentFileName } from "../../../../../../lib/TFICLib.ts";
import { Money } from "../../../../../../lib/Money.ts";
import { BillDocument, CashFlowDirectionEnum, MatchBill, MatchTransaction, RenderTypeEnum, TransactionWithMatchMetadata } from "../types.ts";
import { BankAccountType } from "../../../../../../api/customerBankAccounts/CustomerBankAccount.ts";
import { formatBankAccountIdentifier } from "../../../../../../lib/institutionDisplayNames.ts";
import type {DocumentDirection} from "../../../../../../generated/enums.ts";

export const MATCH_TOLERANCE_CENTS = 15000;

export interface PartialMatchDifferenceLabel {
    prefix: string;
    suffix: string;
    showAmount: boolean;
    get text(): string;
}

function differenceLabel(prefix: string, suffix: string, showAmount: boolean): PartialMatchDifferenceLabel {
    return {
        prefix,
        suffix,
        showAmount,
        get text() {
            return [prefix, suffix].filter(Boolean).join(" ");
        },
    };
}

export function partialMatchDifferenceLabel(
    nrsRemainder: number | null,
    billDirection: DocumentDirection | null,
): PartialMatchDifferenceLabel {
    if (nrsRemainder == null) {
        return differenceLabel("Resterend bedrag", "", false);
    }
    if (nrsRemainder === 0) {
        return differenceLabel("Volledig gematched", "", false);
    }
    if (billDirection == null) {
        return differenceLabel("Nog", "te verrekenen", true);
    }
    if (billDirection === 'sales_invoice') {
        return nrsRemainder > 0
            ? differenceLabel("Nog", "te ontvangen", true)
            : differenceLabel("", "te veel ontvangen", true);
    }
    return nrsRemainder < 0
        ? differenceLabel("Nog", "te betalen", true)
        : differenceLabel("", "overbetaald", true);
}

export function getDocumentRelationName(
    document_direction: DocumentDirection | string | null | undefined,
    scanned_fields?: { seller_name?: string | null; buyer_name?: string | null } | null
): string {
    if (document_direction === 'purchase_invoice') {
        return scanned_fields?.seller_name ?? "N/A";
    }
    return scanned_fields?.buyer_name ?? "N/A";
}

export function convertDocumentToAttachedItem(doc: BillDocument): AttachedItem {
    const relationName = getDocumentRelationName(doc.document_direction, doc.scanned_fields);

    const documentAmount = doc.scanned_fields?.total_amount_payable;
    const signedAmount = getSignedAmountFromDirection(documentAmount, doc.document_direction);
    const adjustedAmount =  { ...documentAmount, signed_amount: signedAmount };

    return {
        id: doc.document_id,
        kind: 'bill',
        relation_name: relationName,
        amount: adjustedAmount,
        metadata: `${doc.scanned_fields?.reference_identifier || "onbekend"} ${cleanDocumentFileName(doc.file_name)}`,
        date: doc.scanned_fields?.normalized_datetime,
        document_category: (doc as any).document_category,
        document_direction: doc.document_direction,
        bill_id: (doc as any).bill_id ?? doc.document_id,
        nrs_amount: doc.nrs_amount ?? null,
    };
}

export function convertTransactionToAttachedItem(tx: Transaction): AttachedItem {
    return {
        id: tx.transaction_id,
        kind: 'transaction',
        relation_name: tx.relation_name,
        amount: tx.total_amount_payable,
        metadata: tx.desc,
        date: tx.booking_date,
        nrs_amount: tx.nrs_amount ?? null,
    };
}

export function convertMatchBillToAttachedItem(bill: MatchBill): AttachedItem {
    return {
        id: bill.document_id,
        kind: 'bill',
        bill_id: bill.bill_id,
        relation_name: bill.relation_name,
        amount: bill.signed_amount,
        date: bill.normalized_date,
        metadata: `${bill.reference_identifier} ${bill.file_name}`,
        document_direction: bill.document_direction,
        nrs_amount: bill.nrs_amount ?? null,
    };
}

export function convertMatchTransactionToAttachedItem(tx: MatchTransaction): AttachedItem {
    return {
        id: tx.transaction_id,
        kind: 'transaction',
        relation_name: tx.relation_name || null,
        amount: tx.signed_amount,
        metadata: tx.desc,
        date: tx.booking_date,
        nrs_amount: tx.nrs_amount ?? null,
    };
}




export function getSignedAmountFromDirection(
    amount: Money | number | null | undefined, 
    flow: CashFlowDirectionEnum | DocumentDirection
): number {
    const amountCents = typeof  amount === 'number' ? amount : amount?.signed_amount ?? 0;

    if (flow === CashFlowDirectionEnum.OUTFLOW ||
        flow === 'purchase_invoice' ) {
        return -amountCents;
    }
    return amountCents;
}

export function formatDate(dateString: string | undefined): string {
    if (!dateString) return "Onbekend";

    try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return "Onbekend";
        return date.toLocaleDateString("nl-NL");
    } catch {
        return "Onbekend";
    }
}

export function isFullyMatched(
    _mainAmount: number,
    attachedItems: AttachedItem[],
    hostNrsAmount?: number | null,
): boolean {
    if (hostNrsAmount == null) return false;
    let attachedSum = 0;
    for (const item of attachedItems) {
        if (item.nrs_amount == null) return false;
        attachedSum += item.nrs_amount;
    }
    return hostNrsAmount + attachedSum === 0;
}

export function partitionMatchIds(
    hostId: string,
    hostIsBill: boolean,
    attachedItems: AttachedItem[],
): { billIds: string[]; transactionIds: string[] } {
    const attachedBills = attachedItems.filter(i => i.kind === 'bill');
    const attachedTxs = attachedItems.filter(i => i.kind === 'transaction');
    if (hostIsBill) {
        return {
            billIds: [hostId, ...attachedBills.map(i => i.bill_id!)],
            transactionIds: attachedTxs.map(i => i.id),
        };
    }
    return {
        billIds: attachedBills.map(i => i.bill_id!),
        transactionIds: [hostId, ...attachedTxs.map(i => i.id)],
    };
}

export function partitionAttachedIds(
    attachedItems: AttachedItem[],
): { billIds: string[]; transactionIds: string[] } {
    return {
        billIds: attachedItems.filter(i => i.kind === 'bill').map(i => i.bill_id!),
        transactionIds: attachedItems.filter(i => i.kind === 'transaction').map(i => i.id),
    };
}

export function isIncoming(amount: number): boolean {
    return amount > 0;
}

export function isOutgoing(amount: number): boolean {
    return amount < 0;
}

export function formatTransactionBankAccountLabel(transaction: TransactionWithMatchMetadata | undefined): string {
    if (!transaction) {
        return '-';
    }

    if (transaction.bank_account_type === BankAccountType.CREDIT_CARD) {
        return transaction.credit_card_number_suffix
            ? `•••• ${transaction.credit_card_number_suffix}`
            : (transaction.bank_account_owner_name ?? '-');
    }

    return formatBankAccountIdentifier(
        transaction.bank_account_iban,
        transaction.institution_name,
    );
}

export function billTypeLabel(
    direction: DocumentDirection | null | undefined,
    nrsAmount: number | null | undefined,
): string | null {
    if (!direction) return null;
    const isCreditNote = (direction === 'sales_invoice' && (nrsAmount ?? 0) < 0)
        || (direction === 'purchase_invoice' && (nrsAmount ?? 0) > 0);
    if (direction === 'purchase_invoice') {
        return isCreditNote ? 'Credit inkoop' : 'Inkoop';
    }
    if (direction === 'sales_invoice') {
        return isCreditNote ? 'Credit verkoop' : 'Verkoop';
    }
    return null;
}

export function canProcessCreditCardSettlement(transaction: Transaction | undefined): boolean {
    if (transaction?.render_type !== RenderTypeEnum.MISSING_DOCUMENT_REQUEST) {
        return false;
    }

    if (!isOutgoing(transaction.total_amount_payable.signed_amount)) {
        return false;
    }

    const accountType = transaction.bank_account_type;
    return accountType === BankAccountType.OPERATIONAL || accountType === BankAccountType.SAVINGS;
}

export function canMatchBillDirections(
    existingDirections: Array<DocumentDirection | null | undefined>,
    incomingDirection?: DocumentDirection | null,
): boolean {
    const families = new Set(
        [...existingDirections, incomingDirection].filter(
            (direction) =>
                direction === 'sales_invoice'
                || direction === 'purchase_invoice',
        ),
    );
    return families.size <= 1;
}

export function canMatch(
    transactionCurrency?: string | null,
    documentCurrency?: string | null,
    hostNrsAmount?: number | null,
    draggedNrsAmount?: number | null,
): boolean {
    if (transactionCurrency && documentCurrency && transactionCurrency !== documentCurrency) {
        return false;
    }

    return hostNrsAmount != null && draggedNrsAmount != null;
}

export function normalizeText(v: string | number | null | undefined) {
    return (v ?? "").toString().toLowerCase();
}

export function convertNumbersToString(num: number) {
    const n = Number(num);
    const abs = Math.abs(n);
    const cents = Math.round(n * 100);
    const euros = n.toFixed(2);
    const eurosAbs = abs.toFixed(2);
    return [String(n), String(abs), String(cents), euros, eurosAbs];
}

export function filterTransactions(sortedTransactions: Transaction[], transactionsSearchQuery: string) {
    const filtered = sortedTransactions.filter(t => {
        const q = normalizeText(transactionsSearchQuery).trim();
        if (!q) return true;
        const tokens = q.split(/\s+/).filter(Boolean);
        const rel = normalizeText(t.relation_name);
        const desc = normalizeText((t as any).desc);
        const docAmount = (t as any).total_amount_payable;
        const amt = docAmount?.signed_amount ? docAmount.signed_amount / 100 : 0;
        const amtStrings = convertNumbersToString(amt).map(normalizeText);
        const searchable = [rel, desc, ...amtStrings];
        return tokens.every(token => searchable.some(s => s.includes(token)));
    });
    return filtered;
}

export function filterDocuments(sortedDocuments: Document[], documentsSearchQuery: string) {
    const filtered = sortedDocuments.filter(d => {
        const q = normalizeText(documentsSearchQuery).trim();
        if (!q) return true;
        const tokens = q.split(/\s+/).filter(Boolean);

        if (d.render_type === RenderTypeEnum.PARTIAL_MATCH) {
            const searchable = [
                normalizeText(d.business_name),
                ...d.bills.map(b => normalizeText(b.relation_name)),
                ...d.bills.map(b => normalizeText(b.reference_identifier)),
                ...d.transactions.map(t => normalizeText(t.relation_name)),
                ...d.transactions.map(t => normalizeText(t.desc)),
            ];
            return tokens.every(token => searchable.some(s => s.includes(token)));
        }

        const name = normalizeText(getDocumentRelationName(d.document_direction, d.scanned_fields));
        const ref = normalizeText(d.scanned_fields?.reference_identifier);
        const payRef = normalizeText(d.scanned_fields?.payment_reference);

        const docAmount = d.scanned_fields?.total_amount_payable;
        const amt = (docAmount?.signed_amount ? Number(docAmount.signed_amount) : 0) * (d.document_direction === 'purchase_invoice' ? -1 : 1) / 100;
        const amtStrings = convertNumbersToString(amt).map(normalizeText);
        const searchable = [name, ref, payRef, ...amtStrings];
        return tokens.every(token => searchable.some(s => s.includes(token)));
    });
    return filtered;
}


export function sortTransactions(
    transactions: Transaction[],
    attachmentsByHost: Record<string, AttachedItem[]>
): Transaction[] {
    return [...transactions].sort((a, b) => {
        const aHasAttachments = (attachmentsByHost[a.transaction_id] || []).length > 0 ||
            a.render_type === RenderTypeEnum.TRANSACTION_MATCH_PART;
        const bHasAttachments = (attachmentsByHost[b.transaction_id] || []).length > 0 ||
            b.render_type === RenderTypeEnum.TRANSACTION_MATCH_PART;
        if (aHasAttachments !== bHasAttachments) return aHasAttachments ? -1 : 1;

        return 0;
    });
}

export function sortDocuments(
    documents: Document[],
    attachmentsByHost: Record<string, AttachedItem[]>
): Document[] {
    return [...documents].sort((a, b) => {
        const aHostId = a.render_type === RenderTypeEnum.PARTIAL_MATCH ? a.match_id : a.document_id;
        const bHostId = b.render_type === RenderTypeEnum.PARTIAL_MATCH ? b.match_id : b.document_id;
        const aHasAttachments = (attachmentsByHost[aHostId] || []).length > 0 ||
            a.render_type === RenderTypeEnum.BILL_MATCH_PART ||
            a.render_type === RenderTypeEnum.PARTIAL_MATCH;
        const bHasAttachments = (attachmentsByHost[bHostId] || []).length > 0 ||
            b.render_type === RenderTypeEnum.BILL_MATCH_PART ||
            b.render_type === RenderTypeEnum.PARTIAL_MATCH;
        if (aHasAttachments !== bHasAttachments) return aHasAttachments ? -1 : 1;

        return 0;
    });
}
